/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable spellcheck/spell-checker */
/**
 *
 * Safe, race-free wrapper around IndexedDB for:
 *  - getAllRows(dbName, objectStoreBaseName)
 *  - addData(dbName, objectStoreName, data)
 *  - removeObjectStores(dbName, objectStoreNamesArray)
 *
 * Important:
 *  - This code serializes DB version upgrades per DB name to avoid blocked race conditions.
 *  - It only performs an upgrade if the requested object store does not exist.
 */

const dbUpgradeLocks = new Map(); // dbName -> Promise that resolves when current upgrade completes

/**
 * Helper: run an async function while holding a simple lock per dbName.
 */
async function withUpgradeLock(dbName, fn) {
    const prev = dbUpgradeLocks.get(dbName) || Promise.resolve();
    let resolveHere;
    const next = new Promise((res) => (resolveHere = res));
    dbUpgradeLocks.set(
        dbName,
        prev.then(() => next)
    );
    try {
        // wait for previous to finish
        await prev;

        return await fn();
    } finally {
        // release
        resolveHere();
        // if current entry resolved to itself, cleanup
        if (dbUpgradeLocks.get(dbName) === next) {
            dbUpgradeLocks.delete(dbName);
        }
    }
}

/**
 * Open DB without causing upgrade (returns db instance)
 */
function openDb(dbName) {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(dbName);
        /**
         *
         */
        req.onsuccess = (e) => resolve(e.target.result);
        /**
         *
         */
        /* istanbul ignore next */
        req.onerror = (e) => reject(e.target.error || e.target.errorCode);
    });
}

/**
 * Get current DB version (opens without upgrade)
 */
async function getDbVersion(dbName) {
    const db = await openDb(dbName).catch(() => null);
    if (!db) {
        return 0;
    }
    const v = db.version;
    db.close();

    return v;
}

/**
 * Ensure an objectStore exists. If not, performs a single version upgrade to create it.
 * This operation is serialized per-dbName (so concurrent calls won't collide).
 *
 * @param {string} dbName
 * @param {string} storeName
 * @returns {Promise<void>}
 */
async function ensureObjectStore(dbName, storeName) {
    // Fast path: open DB and check for store
    let db;
    try {
        db = await openDb(dbName);
        /* istanbul ignore next */
    } catch (e) {
        // If DB doesn't exist yet, version will be 0 - proceed to create
        db = null;
    }

    if (db) {
        if (db.objectStoreNames.contains(storeName)) {
            db.close();

            return;
        }
        db.close();
    }

    // Serialize upgrades so multiple callers don't race
    await withUpgradeLock(dbName, async () => {
        // Double-check inside lock (another caller may have created store while we waited)
        let currentDb = null;
        try {
            currentDb = await openDb(dbName);
            /* istanbul ignore next */
        } catch (e) {
            currentDb = null;
        }

        if (currentDb) {
            if (currentDb.objectStoreNames.contains(storeName)) {
                currentDb.close();

                return;
            }
            const curVersion = currentDb.version;
            currentDb.close();
            // open upgrade request
            await new Promise((resolve, reject) => {
                const upgradeReq = indexedDB.open(dbName, curVersion + 1);

                /**
                 *
                 */
                upgradeReq.onupgradeneeded = (ev) => {
                    const idb = ev.target.result;
                    if (!idb.objectStoreNames.contains(storeName)) {
                        idb.createObjectStore(storeName, { "keyPath": "id" });
                    }
                };

                /**
                 *
                 */
                upgradeReq.onsuccess = (ev) => {
                    const idb = ev.target.result;
                    idb.close();
                    resolve();
                };

                /**
                 *
                 */
                upgradeReq.onerror = (ev) => {
                    try {
                        ev.target?.result?.close();
                        /* istanbul ignore next */
                    } catch (e) {
                        /* empty */
                    }
                    /* istanbul ignore next */
                    reject(ev.target.error || ev.target.errorCode);
                };
                // If blocked occurs, still let onsuccess or onerror handle result.
            });
        } else {
            // DB doesn't exist yet: create DB at version 1 and create the store
            await new Promise((resolve, reject) => {
                const createReq = indexedDB.open(dbName, 1);
                /**
                 *
                 */
                createReq.onupgradeneeded = (ev) => {
                    const idb = ev.target.result;
                    if (!idb.objectStoreNames.contains(storeName)) {
                        idb.createObjectStore(storeName, { "keyPath": "id" });
                    }
                };

                /**
                 *
                 */
                createReq.onsuccess = (ev) => {
                    const idb = ev.target.result;
                    idb.close();
                    resolve();
                };

                /**
                 *
                 */
                /* istanbul ignore next */
                createReq.onerror = (ev) => {
                    try {
                        ev.target?.result?.close();
                    } catch (e) {
                        /* empty */
                    }
                    reject(ev.target.error || ev.target.errorCode);
                };
            });
        }
    });
}

/**
 * Get All Rows for an objectStore base name -- returns combined rows from any objectStore
 * whose name contains objectStoreName (this preserves your chunking approach)
 * Resolves with data array or rejects with { dbVersion, status: false } if the *base* store is not present.
 *
 * @param {string} dbName
 * @param {string} objectStoreName  - // base name, e.g. "scenario_results_15"
 * @returns {Promise<Array| null>}
 */
export const getAllRows = (dbName, objectStoreName) =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
        let db;
        try {
            db = await openDb(dbName);
            /* istanbul ignore next */
        } catch (err) {
            // DB doesn't exist yet => same signal used previously: no store
            return reject({ "dbVersion": 0, "status": false });
        }

        try {
            const objectStores = Array.from(db.objectStoreNames);
            // find relevant stores that *include* the base name (chunking)
            const relevantStoreNames = objectStores.filter((s) => s.includes(objectStoreName));

            if (!relevantStoreNames.length) {
                const version = db.version;
                db.close();

                /* istanbul ignore next */
                return reject({ "dbVersion": version, "status": false });
            }

            const results = [];
            let completed = 0;
            for (const storeName of relevantStoreNames) {
                const tx = db.transaction(storeName, "readonly");
                const store = tx.objectStore(storeName);
                const getAllReq = store.getAll();

                /**
                 *
                 */
                getAllReq.onsuccess = (ev) => {
                    results.push(...(ev.target.result || []));
                };

                /**
                 *
                 */
                getAllReq.onerror = (ev) => {
                    // log and continue — but we'll mark it as completed
                    // nothing to do: treat missing chunk as empty
                };

                /**
                 *
                 */
                tx.oncomplete = () => {
                    completed++;
                    if (completed === relevantStoreNames.length) {
                        db.close();
                        // If results is empty, return null to mirror your previous code
                        resolve(results.length ? results : null);
                    }
                };

                /**
                 *
                 */
                tx.onerror = (ev) => {
                    completed++;
                    if (completed === relevantStoreNames.length) {
                        db.close();
                        resolve(results.length ? results : null);
                    }
                };
            }
        } catch (e) {
            try {
                db.close();
                /* istanbul ignore next */
            } catch (er) {
                /* empty */
            }
            reject(e);
        }
    });

/**
 * Add Data to objectStore (safe)
 * - Ensures objectStore exists (creates via serialized upgrade if needed)
 * - Uses transaction and waits for tx.oncomplete before closing
 *
 * @param {string} dbName
 * @param {string} objectStoreName
 * @param {Array<object>} data
 * @returns {Promise<void>}
 */
export const addData = async (dbName, objectStoreName, data) => {
    // ensure store exists
    await ensureObjectStore(dbName, objectStoreName);

    // open DB and write in a transaction
    const db = await openDb(dbName);

    return new Promise((resolve, reject) => {
        let tx;
        try {
            tx = db.transaction(objectStoreName, "readwrite");
        } catch (err) {
            // weird state: maybe store missing
            db.close();

            return reject(err);
        }
        const store = tx.objectStore(objectStoreName);

        let anyError = false;

        for (const record of data) {
            // use put instead of add to avoid duplicate key errors; add would throw if id already exists.
            // If you prefer to fail when duplicate, switch to store.add(record)
            const req = store.put(record);
            /**
             *
             */
            req.onerror = (ev) => {
                anyError = true;
                // continue; we'll handle in tx.onerror/oncomplete
            };
        }

        /**
         *
         */
        /* istanbul ignore next */
        tx.oncomplete = () => {
            db.close();
            if (anyError) {
                // we had some record-level errors but transaction committed; still resolve so caller can proceed
                resolve();
            } else {
                resolve();
            }
        };

        /**
         *
         */
        /* istanbul ignore next */
        tx.onerror = (ev) => {
            db.close();
            reject(ev.target?.error || ev.target?.errorCode || ev);
        };

        /**
         *
         */
        /* istanbul ignore next */
        tx.onabort = (ev) => {
            db.close();
            reject(ev.target?.error || ev.target?.errorCode || ev);
        };
    });
};

/**
 * Remove Object Stores (safe)
 * - Serializes the upgrade that deletes the requested stores
 *
 * @param {string} dbName
 * @param {Array<string>} objectStores - (base names or exact names)
 * @returns {Promise<void>}
 */
export const removeObjectStores = (dbName, objectStoresArray) =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
        let db;
        try {
            db = await openDb(dbName);
            /* istanbul ignore next */
        } catch (err) {
            // DB doesn't exist => nothing to remove
            return resolve();
        }

        const objectStores = Array.from(db.objectStoreNames);
        const toDelete = [];

        // match chunked stores (those that include the base name)
        for (const baseName of objectStoresArray) {
            for (const storeName of objectStores) {
                if (storeName.includes(baseName)) {
                    toDelete.push(storeName);
                }
            }
        }

        db.close();

        if (!toDelete.length) {
            return resolve();
        }

        // do upgrade serialized
        try {
            await withUpgradeLock(dbName, async () => {
                const curVersion = await getDbVersion(dbName);
                await new Promise((res, rej) => {
                    const upgradeReq = indexedDB.open(dbName, curVersion + 1);
                    /**
                     *
                     */
                    upgradeReq.onupgradeneeded = (ev) => {
                        const idb = ev.target.result;
                        for (const s of toDelete) {
                            if (idb.objectStoreNames.contains(s)) {
                                idb.deleteObjectStore(s);
                            }
                        }
                    };

                    /**
                     *
                     */
                    upgradeReq.onsuccess = (ev) => {
                        ev.target.result.close();
                        res();
                    };

                    /**
                     *
                     */
                    upgradeReq.onerror = (ev) => {
                        try {
                            ev.target?.result?.close();
                        } catch (e) {
                            /* empty */
                        }
                        rej(ev.target.error || ev.target.errorCode);
                    };
                });
            });
            resolve();
        } catch (e) {
            reject(e);
        }
    });

/**
 * Delete all Databases (you had this before)
 */
export const deleteAllDatabases = async () => {
    if (!indexedDB.databases) {
        // older browsers might not support listing; no-op
        return;
    }
    const dbs = await indexedDB.databases();
    for (const db of dbs) {
        try {
            indexedDB.deleteDatabase(db.name);
            /* istanbul ignore next */
        } catch (e) {
            /* empty */
        }
    }
};
